import { FormattedMessage, useIntl } from 'react-intl'
import { useSelector } from 'react-redux';
import _get from 'lodash/get';
import Card from '@/entities/Card';
import Button from '@/shared/ui/Button';
import { openNewTab } from '@/shared/utils/openLink'
import Table, { DateCell, TextCell } from '@/shared/ui/Table';
import { IconList, IconArrowTo } from '@/shared/ui/Icons';
import StatusIcon from '@/shared/ui/StatusIcon';
import NewTabLink from '@/shared/ui/NewTabLink';
import { useGetTransactionsSharedQuery } from '@/shared/api/Payouts';
import { 
  ITEMS_IN_WIDGET, 
  REJECTED_STATUSES,
  SORT_BY,
  ORDER,
  getStatusIconVariant, 
  getStatusMessage,
} from '~/shared/Payouts';
import { selectPollingInterval } from "@/processes/Settings";
import { useNavigation, VIEWS } from '@/processes/Router';
import { useExplorer } from '@/processes/PoolStats';
import { useMinerAccount } from "@/processes/MinerAccounts";

type PayoutsWidgetProps = {
  className: string;
}

const PayoutsWidget = (props: PayoutsWidgetProps) => {
  const { className } = props;
  const intl = useIntl();
  const { updateURLQueryParams } = useNavigation();
  const pollingInterval = useSelector(selectPollingInterval);
  const { miner, coin } = useMinerAccount();
  const { explorerLink } = useExplorer({ coin });

  const { 
    data = {},
    isSuccess,
  } = useGetTransactionsSharedQuery({ 
    miner,
    coin,
    limit: 10,
    sortBy: `${ORDER.DESC}${SORT_BY.CREATED_AT}`,
    offset: 0,
  }, { pollingInterval, skip: !(miner && coin) })
  const transactions = _get(data, 'items', []).filter((transaction: any) => !REJECTED_STATUSES.includes(transaction.status));
  const total = _get(data, 'total', 0);
  
  return (
    <Card 
      className={className} 
      title={intl.formatMessage({ id: 'payouts.history' })}
      actions={
        total > 0 ? (
          <Button 
            isOverlay={true}
            onClick={() => updateURLQueryParams({ view: VIEWS.PAYOUTS })}
            prefixIcon={(<IconList/>)}
          >{intl.formatMessage({ id: 'details' })}</Button>
        ) : null
      }
    >
      <Table
        classNameRoot='mt-6'
        stripedRows={true}
        withHeader={false}
        data={transactions.slice(0, ITEMS_IN_WIDGET)}
        onRowClick={(row) => {
          row.txHash && openNewTab(explorerLink(row.txHash));
        }}
        columns={[
          {
            Header: 'Date',
            accessor: 'createdAt',
            disableSortBy: true,
            Cell: DateCell,
          }, 
          {
            Header: 'Amount',
            accessor: 'amount',
            disableSortBy: true,
            Cell: ({ value }: any) => <TextCell value={value} subValue={coin}/>
          }, 
          {
            Header: 'Actions',
            accessor: 'status',
            disableSortBy: true,
            className: 'w-[70px]',
            Cell: (props: any) => {
              const { value, row, data } = props;
              const hash = _get(data, `${row.index}.txHash`, null);
              return (
                <span className='flex items-center justify-end'>
                  <StatusIcon
                    className='w-[14px] h-[14px]'
                    status={getStatusIconVariant(value)}
                  />
                  <div className="ml-2 w-6 inline-flex items-center">
                    {hash ? (
                      <NewTabLink/>
                    ) : null}
                  </div>
                </span>
              )
            }
          }
        ]}
      />
    </Card>
  );
}

export default PayoutsWidget;
